import { List, ListItem, ListItemIcon, ListItemText, Box, Divider, Typography, IconButton, Tooltip } from '@mui/material';
import { Dashboard, CloudDownload, Settings, Notifications, Logout, AdminPanelSettings, ChevronLeft, ChevronRight } from '@mui/icons-material';
import Badge from '@mui/material/Badge';
import BrandLogo from './auth/BrandLogo';
import { useAuth } from '../contexts/AuthContext';
import { getJwtPayload } from '../utils/jwt';

//ceci est la sidebar qui affiche les icones et les labels des différents menus
const SIDEBAR_DYNAMIC_SX = {
  '--sidebar-padding': '20px',
  '--sidebar-logo-size': '52px',
  '--sidebar-brand-size': '22px',
  '--sidebar-item-height': '52px',
  '--sidebar-item-padding-y': '12px',
  '--sidebar-item-padding-x': '16px',
  '--sidebar-item-gap': '12px',
  '--sidebar-icon-size': '24px',
  '--sidebar-label-size': '15px',
  '--sidebar-user-label-size': '11px',
  '--sidebar-user-name-size': '14px',
  '--sidebar-user-email-size': '12px',
  '--sidebar-logout-size': '15px',
  '--sidebar-section-gap': '14px',

  '@media (max-height: 820px)': {
    '--sidebar-padding': '16px',
    '--sidebar-logo-size': '46px',
    '--sidebar-brand-size': '20px',
    '--sidebar-item-height': '46px',
    '--sidebar-item-padding-y': '9px',
    '--sidebar-item-padding-x': '13px',
    '--sidebar-item-gap': '10px',
    '--sidebar-icon-size': '21px',
    '--sidebar-label-size': '14px',
    '--sidebar-user-label-size': '10px',
    '--sidebar-user-name-size': '13px',
    '--sidebar-user-email-size': '11px',
    '--sidebar-logout-size': '14px',
    '--sidebar-section-gap': '10px',
  },

  '@media (max-height: 700px)': {
    '--sidebar-padding': '12px',
    '--sidebar-logo-size': '38px',
    '--sidebar-brand-size': '18px',
    '--sidebar-item-height': '40px',
    '--sidebar-item-padding-y': '7px',
    '--sidebar-item-padding-x': '11px',
    '--sidebar-item-gap': '8px',
    '--sidebar-icon-size': '19px',
    '--sidebar-label-size': '13px',
    '--sidebar-user-label-size': '9px',
    '--sidebar-user-name-size': '12px',
    '--sidebar-user-email-size': '10px',
    '--sidebar-logout-size': '13px',
    '--sidebar-section-gap': '7px',
  },

  '@media (max-height: 620px)': {
    '--sidebar-padding': '10px',
    '--sidebar-logo-size': '34px',
    '--sidebar-brand-size': '16px',
    '--sidebar-item-height': '36px',
    '--sidebar-item-padding-y': '6px',
    '--sidebar-item-padding-x': '10px',
    '--sidebar-item-gap': '7px',
    '--sidebar-icon-size': '18px',
    '--sidebar-label-size': '12px',
    '--sidebar-user-label-size': '8px',
    '--sidebar-user-name-size': '11px',
    '--sidebar-user-email-size': '9px',
    '--sidebar-logout-size': '12px',
    '--sidebar-section-gap': '5px',
  },
};

const Sidebar = ({ onMenuClick, currentMenu, alertCount, isCollapsed, onToggleCollapsed }) => {
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
  };

  const getMenuStyle = (menu) => ({
    minHeight: 'var(--sidebar-item-height)',
    backgroundColor:
      currentMenu === menu
        ? 'rgba(255, 255, 255, 0.1)'
        : 'transparent',
    color: '#fff',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    borderRadius: '12px',
    paddingY: 0,
    paddingX: isCollapsed
      ? '8px'
      : 'var(--sidebar-item-padding-x)',
    marginBottom: 'var(--sidebar-section-gap)',
    justifyContent: isCollapsed ? 'center' : 'flex-start',
    alignItems: 'center',
    boxSizing: 'border-box',

    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
  });


  const token = localStorage.getItem('token');
  const payload = getJwtPayload(token);
  const isAdmin = payload?.role === 'admin';

  const menuItems = [
    { id: 'dashboard', icon: <Dashboard sx={{ color: '#FFFFFF' }} />, label: 'Tableau de bord' },
    { id: 'download', icon: <CloudDownload sx={{ color: '#FFFFFF' }} />, label: 'Télécharger les données' },
    { id: 'alerts', icon: <Notifications sx={{ color: '#FFFFFF' }} />, label: 'Alertes', badge: alertCount },
    { id: 'settings', icon: <Settings sx={{ color: '#FFFFFF' }} />, label: 'Paramètres' },

    ...(isAdmin
      ? [{ id: 'admin', icon: <AdminPanelSettings sx={{ color: '#FFFFFF' }} />, label: 'Administration' }]
      : []),
  ];

  return (
    <Box
      sx={{
        ...SIDEBAR_DYNAMIC_SX,

        width: isCollapsed ? 72 : 240,
        height: '100dvh',
        maxHeight: '100dvh',
        boxSizing: 'border-box',

        padding: isCollapsed
          ? '10px'
          : 'var(--sidebar-padding)',

        position: 'fixed',
        left: 0,
        top: 0,
        zIndex: 1200,

        backgroundColor: '#1a1b2e',
        boxShadow: '2px 0 10px rgba(0, 0, 0, 0.3)',
        borderRight: '1px solid rgba(255, 255, 255, 0.1)',

        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',

        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          padding: isCollapsed ? '4px' : '6px',
          marginBottom: 'var(--sidebar-section-gap)',
          justifyContent: isCollapsed ? 'center' : 'space-between',
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            transform: {
              md: 'scale(0.92)',
            },

            '@media (max-height: 700px)': {
              transform: 'scale(0.82)',
            },

            '@media (max-height: 620px)': {
              transform: 'scale(0.72)',
            },

            transformOrigin: isCollapsed ? 'center' : 'left center',
          }}
        >
          <BrandLogo
            iconSize={isCollapsed ? 40 : 48}
            showText={!isCollapsed}
            fontSize="1.2rem"
            textColor="#fff"
          />
        </Box>

        {isCollapsed && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: 'var(--sidebar-section-gap)',
              flexShrink: 0,
            }}
          >
            <IconButton
              onClick={onToggleCollapsed}
              size="small"
              sx={{
                color: '#fff',
                width: {
                  md: 34,
                },
                height: {
                  md: 34,
                },

                '@media (max-height: 700px)': {
                  width: 30,
                  height: 30,
                },

                '@media (max-height: 620px)': {
                  width: 28,
                  height: 28,
                },
              }}
            >
              <ChevronRight
                sx={{
                  fontSize: 'var(--sidebar-icon-size)',
                }}
              />
            </IconButton>
          </Box>
        )}
      </Box>
      {isCollapsed && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <IconButton onClick={onToggleCollapsed} size="small" sx={{ color: '#fff' }}>
            <ChevronRight />
          </IconButton>
        </Box>
      )}

      <List
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          paddingY: 'var(--sidebar-section-gap)',

          '&::-webkit-scrollbar': {
            width: '5px',
          },

          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(255,255,255,0.04)',
          },

          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255,255,255,0.18)',
            borderRadius: '999px',
          },
        }}
      >
        {menuItems.map(({ id, icon, label, badge }) => (
          <Tooltip key={id} title={isCollapsed ? label : ''} placement="right">
            <ListItem onClick={() => onMenuClick(id)} sx={getMenuStyle(id)}>
              <ListItemIcon
                sx={{
                  minWidth: isCollapsed ? 0 : 34,
                  justifyContent: 'center',
                  color: '#fff',

                  '& svg': {
                    fontSize: 'var(--sidebar-icon-size)',
                  },
                }}
              >
                {badge ? (
                  <Badge badgeContent={badge} color="error">
                    {icon}
                  </Badge>
                ) : (
                  icon
                )}
              </ListItemIcon>

              {!isCollapsed && (
                <ListItemText
                  primary={label}
                  primaryTypographyProps={{
                    sx: {
                      color: '#fff',
                      fontSize: 'var(--sidebar-label-size)',
                      fontWeight: 700,
                      lineHeight: 1.25,
                      whiteSpace: 'normal',
                    },
                  }}
                />
              )}
            </ListItem>
          </Tooltip>
        ))}
      </List>

      {/* Section utilisateur et déconnexion */}
      <Box
        sx={{
          flexShrink: 0,
          marginTop: 'var(--sidebar-section-gap)',
          paddingTop: 'var(--sidebar-section-gap)',
          borderTop: '1px solid rgba(255,255,255,0.08)',
          overflow: 'hidden',
        }}
      >
        {!isCollapsed && (
          <Box
            sx={{
              paddingX: '6px',
              paddingBottom: 'var(--sidebar-section-gap)',
              overflow: 'hidden',
            }}
          >
            <Typography
              sx={{
                color: '#aaa',
                fontSize: 'var(--sidebar-user-label-size)',
                lineHeight: 1.15,
              }}
            >
              Connecté en tant que
            </Typography>

            <Typography
              sx={{
                color: '#fff',
                fontWeight: 800,
                fontSize: 'var(--sidebar-user-name-size)',
                lineHeight: 1.15,
                marginTop: '3px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user?.firstName} {user?.lastName}
            </Typography>

            <Typography
              sx={{
                color: '#aaa',
                fontSize: 'var(--sidebar-user-email-size)',
                lineHeight: 1.15,
                marginTop: '3px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user?.email}
            </Typography>
          </Box>
        )}
        {/* Section utilisateur et déconnexion */}
        <Tooltip title={isCollapsed ? 'Déconnexion' : ''} placement="right">
          <ListItem
            onClick={handleLogout}
            sx={{
              minHeight: 'var(--sidebar-item-height)',
              backgroundColor: 'transparent',
              color: '#FF5722',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              borderRadius: '12px',
              paddingY: 'var(--sidebar-item-padding-y)',
              paddingX: isCollapsed
                ? '8px'
                : 'var(--sidebar-item-padding-x)',
              justifyContent: isCollapsed ? 'center' : 'flex-start',
              boxSizing: 'border-box',

              '&:hover': {
                backgroundColor: 'rgba(255, 87, 34, 0.1)',
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: isCollapsed ? 0 : 34,
                justifyContent: 'center',

                '& svg': {
                  fontSize: 'var(--sidebar-icon-size)',
                },
              }}
            >
              <Logout sx={{ color: '#FF5722' }} />
            </ListItemIcon>

            {!isCollapsed && (
              <ListItemText
                primary="Déconnexion"
                primaryTypographyProps={{
                  sx: {
                    color: '#FF5722',
                    fontSize: 'var(--sidebar-logout-size)',
                    fontWeight: 800,
                    lineHeight: 1.2,
                  },
                }}
              />
            )}
          </ListItem>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default Sidebar;

